import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api.js";
import VoiceRecorder from "../components/VoiceRecorder.jsx";

// Peer respondent form. Separate route, no login, short, mobile-first, voice on each.
// The respondent picks the language; text is gendered by the leader's pronouns.

const LANGS = [
  { code: "fr", label: "Français" },
  { code: "en", label: "English" },
  { code: "pt", label: "Português" },
  { code: "es", label: "Español" },
];

const PRON = {
  en: { he: { subj: "he", obj: "him", poss: "his" }, she: { subj: "she", obj: "her", poss: "her" }, they: { subj: "they", obj: "them", poss: "their" } },
  fr: { he: { subj: "il", obj: "le", poss: "son" }, she: { subj: "elle", obj: "la", poss: "son" }, they: { subj: "iel", obj: "le/la", poss: "son" } },
  pt: { he: { subj: "ele", obj: "o", poss: "seu" }, she: { subj: "ela", obj: "a", poss: "seu" }, they: { subj: "elu", obj: "o/a", poss: "seu" } },
  es: { he: { subj: "él", obj: "lo", poss: "su" }, she: { subj: "ella", obj: "la", poss: "su" }, they: { subj: "elle", obj: "lo/la", poss: "su" } },
};

const OPERATOR = "Quentin Franque";
const UI = {
  en: { title: "A minute on {name}?", start: "Start", name: "Your name", email: "Your email", hi: "Welcome", aboutSub: "A few quick questions about {name}.", type: "Type here…", send: `Send to ${OPERATOR}`, sending: "Sending…", thanksTitle: "Thank you!", thanksBody: "Thanks so much, this really helps. Keep an eye on {name} over the next few weeks, you'll see the way {poss} communication grows.", lang: "Language",
    intro: "{name} picked you to answer a few short questions that'll help sharpen {poss} LinkedIn strategy.", voiceHint: "Or answer by voice:" },
  fr: { title: "Une minute pour {name} ?", start: "Commencer", name: "Ton nom", email: "Ton email", hi: "Bienvenue", aboutSub: "Quelques questions rapides sur {name}.", type: "Écris ici…", send: `Envoyer à ${OPERATOR}`, sending: "Envoi…", thanksTitle: "Merci !", thanksBody: "Merci beaucoup, ça va vraiment aider. Reviens dans quelques semaines voir comment la communication de {name} a évolué.", lang: "Langue",
    intro: "{name} t'a choisi pour répondre à quelques questions qui l'aideront à affiner sa stratégie LinkedIn.", voiceHint: "Ou réponds à l'oral :" },
  pt: { title: "Um minuto sobre {name}?", start: "Começar", name: "Seu nome", email: "Seu email", hi: "Bem-vindo", aboutSub: "Algumas perguntas rápidas sobre {name}.", type: "Escreva aqui…", send: `Enviar para ${OPERATOR}`, sending: "Enviando…", thanksTitle: "Obrigado!", thanksBody: "Muito obrigado, isso ajuda muito. Volte em algumas semanas para ver como a comunicação de {name} evoluiu.", lang: "Idioma",
    intro: "{name} escolheu você para responder a algumas perguntas que vão ajudar a melhorar a sua estratégia de LinkedIn.", voiceHint: "Ou responda por áudio:" },
  es: { title: "¿Un minuto sobre {name}?", start: "Empezar", name: "Tu nombre", email: "Tu email", hi: "Hola", aboutSub: "Unas preguntas rápidas sobre {name}.", type: "Escribe aquí…", send: `Enviar a ${OPERATOR}`, sending: "Enviando…", thanksTitle: "¡Gracias!", thanksBody: "Muchas gracias, esto ayuda muchísimo. Vuelve en unas semanas para ver cómo ha evolucionado la comunicación de {name}.", lang: "Idioma",
    intro: "{name} te eligió para responder unas preguntas que ayudarán a mejorar su estrategia de LinkedIn.", voiceHint: "O responde por voz:" },
};

const QUESTIONS = [
  { key: "comm_daily", t: {
    fr: "Comment décrirais-tu la façon dont {name} communique au quotidien ?",
    en: "How would you describe the way {name} communicates day to day?",
    pt: "Como você descreveria o jeito que {name} se comunica no dia a dia?",
    es: "¿Cómo describirías la forma en que {name} se comunica en el día a día?" } },
  { key: "warm_follow", t: {
    fr: "Qu'est-ce qui fait que les gens s'attachent à {name}, ou ont envie de {obj} suivre ?",
    en: "What makes people warm to {name}, or want to follow {obj}?",
    pt: "O que faz as pessoas gostarem de {name} e quererem seguir {name}?",
    es: "¿Qué hace que la gente conecte con {name} o quiera seguir a {name}?" } },
  { key: "verbal_tics", t: {
    fr: "Des tics de langage ou des expressions que tu as remarqués dans sa façon de parler ?",
    en: "Any verbal tics or turns of phrase you've noticed in how {name} talks?",
    pt: "Algum vício de linguagem ou jeito de falar que você percebeu em {name}?",
    es: "¿Algún tic de lenguaje o forma de hablar que hayas notado en {name}?" } },
  { key: "light_up", t: {
    fr: "Quand {name} parle de son travail, qu'est-ce qui l'anime le plus ?",
    en: "When {name} talks about {poss} work, what lights {obj} up?",
    pt: "Quando {name} fala do trabalho, o que mais empolga {name}?",
    es: "Cuando {name} habla de su trabajo, ¿qué es lo que más le entusiasma?" } },
  { key: "explain", t: {
    fr: "Qu'est-ce que {name} explique mieux que personne ?",
    en: "What does {name} explain better than anyone you know?",
    pt: "O que {name} explica melhor do que qualquer pessoa que você conhece?",
    es: "¿Qué explica {name} mejor que nadie que conozcas?" } },
  { key: "believe", t: {
    fr: "Y a-t-il quelque chose que {name} croit et que la plupart des gens de son domaine ne partagent pas ? Seulement si ça te vient.",
    en: "Anything {name} believes that most people in {poss} field don't? Only if it comes to mind.",
    pt: "Tem algo em que {name} acredita e que a maioria das pessoas da área não? Só se vier à cabeça.",
    es: "¿Hay algo que {name} cree y que la mayoría de su sector no? Solo si se te ocurre." } },
  { key: "one_line", t: {
    fr: "En une ligne, pourquoi suivre {name} en ligne ?",
    en: "In one line, why should someone follow {name} online?",
    pt: "Em uma linha, por que alguém deveria seguir {name} online?",
    es: "En una línea, ¿por qué seguir a {name} en internet?" } },
];

export default function MirrorForm() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);
  const [lang, setLang] = useState("en");
  const [step, setStep] = useState("intro"); // intro | form | done
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [answers, setAnswers] = useState({});
  const [sending, setSending] = useState(false);

  useEffect(() => { api.peerLoad(token).then(setMeta).catch((e) => setError(e.message)); }, [token]);

  if (error) return <Wrap title="This link isn't valid">{error}</Wrap>;
  if (!meta) return <Wrap title="…" />;

  const fn = meta.leader_first_name;
  const pron = (PRON[lang] || PRON.en)[meta.gender] || (PRON[lang] || PRON.en).they;
  const sub = (s) => (s || "").replace(/\{name\}/g, fn).replace(/\{subj\}/g, pron.subj).replace(/\{obj\}/g, pron.obj).replace(/\{poss\}/g, pron.poss);
  const ui = UI[lang];

  if (step === "done") return (
    <div className="hero hero--full">
      <img className="hero__img" src="/img/done.jpg" alt="" />
      <div className="hero__scrim" />
      <div className="qf-wrap hero__content">
        <div className="center-narrow qf-stack--lg">
          <h1 className="qf-title">{sub(ui.thanksTitle)}</h1>
          <p className="qf-lead">{sub(ui.thanksBody)}</p>
        </div>
      </div>
    </div>
  );

  if (step === "intro") {
    return (
      <div className="app-shell">
        <div className="hero hero--welcome">
          <img className="hero__img" src="/img/welcome.jpg" alt="" />
          <div className="hero__scrim" />
          <div className="qf-wrap hero__content">
            <h1 className="qf-title">{sub(ui.title)}</h1>
          </div>
        </div>
        <div className="qf-wrap qf-section">
          <div className="overview" style={{ maxWidth: 620 }}>
            <div className="hstack qf-between">
              <span className="qf-label">{ui.lang}</span>
              <select className="input" style={{ width: "auto" }} value={lang} onChange={(e) => setLang(e.target.value)}>
                {LANGS.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
              </select>
            </div>
            <p className="qf-lead">{sub(ui.intro)}</p>
            <div className="hstack">
              <input className="input" placeholder={ui.name} value={name} onChange={(e) => setName(e.target.value)} />
              <input className="input" placeholder={ui.email} value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div>
              <button className="qf-btn qf-btn--primary" disabled={!name.trim() || !email.trim()} onClick={() => setStep("form")}>{ui.start}</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const setText = (key, text) => setAnswers((a) => ({ ...a, [key]: { ...a[key], text } }));
  const setAudio = (key, audio_key) => setAnswers((a) => ({ ...a, [key]: { ...a[key], audio_key } }));
  const submit = async () => {
    setSending(true);
    try { await api.peerSubmit({ token, respondent_name: name, respondent_email: email, answers }); setStep("done"); }
    finally { setSending(false); }
  };
  const respondentFirst = name.trim().split(/\s+/)[0];

  return (
    <div className="app-shell qf-section">
      <div className="qf-wrap center-narrow overview" style={{ maxWidth: 620 }}>
        <div className="cover"><img src="/img/band2.jpg" alt="" /></div>
        <div>
          <div className="dash__eyebrow">{ui.hi}, {respondentFirst}</div>
          <h1 className="qf-title">{sub(ui.aboutSub)}</h1>
        </div>
        {QUESTIONS.map((q) => (
          <div className="qf-card qf-stack" key={q.key}>
            <div style={{ fontFamily: "var(--qf-serif)", fontSize: "1.15rem" }}>{sub(q.t[lang])}</div>
            <textarea className="input" placeholder={ui.type} value={answers[q.key]?.text || ""}
              onChange={(e) => setText(q.key, e.target.value)} />
            <span className="muted-sm">{ui.voiceHint}</span>
            <VoiceRecorder token={token} peer question_key={q.key} onUploaded={({ key }) => setAudio(q.key, key)} />
          </div>
        ))}
        <div>
          <button className="qf-btn qf-btn--primary" onClick={submit} disabled={sending}>
            {sending ? ui.sending : sub(ui.send)}
          </button>
        </div>
      </div>
    </div>
  );
}

function Wrap({ title, children }) {
  return (
    <div className="app-shell qf-section">
      <div className="qf-wrap center-narrow qf-stack">
        <h1 className="qf-title">{title}</h1>
        {typeof children === "string" ? <p className="qf-lead">{children}</p> : children}
      </div>
    </div>
  );
}
